import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  FileText, Upload, Sparkle, ChartLineUp, FilePdf,
  CheckCircle, Receipt, UserCircle, Plus, ArrowRight,
  Archive, ArrowCounterClockwise, Eye, Calculator,
  Download, CurrencyEur, ChatCenteredText, Clock,
} from '@phosphor-icons/react';
import SpiegaVoceBar from '../components/SpiegaVoceBar';
import { Textarea } from '../components/ui/textarea';

const API = process.env.REACT_APP_BACKEND_URL;

export default function FattureOcrPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isSubAgente = user?.role === 'commerciale' && user?.sales_tier === 'sub_agente';
  const canApplyExtraScontoDirectly = isAdmin
    || (user?.role === 'commerciale' && ['agente', 'canale_vendita'].includes(user?.sales_tier));
  const [analyses, setAnalyses] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [active, setActive] = useState(null); // current analysis
  const [step, setStep] = useState('list'); // list | review | simulate | done
  const [review, setReview] = useState(null);
  const [consensi, setConsensi] = useState({ trattamento_dati: false, rivendibilita_dati: false, marketing: false });
  const [selectedOfferId, setSelectedOfferId] = useState('');
  const [simulation, setSimulation] = useState(null);
  const [archiveView, setArchiveView] = useState(false);
  const [showFormula, setShowFormula] = useState(false);
  // Sprint 47 - upload multi-file + selezione bulk
  const [uploadQueue, setUploadQueue] = useState([]); // {id, filename, status, error, jobId}
  const [selectedAnalyses, setSelectedAnalyses] = useState(new Set());
  // Sprint B - Extra sconto agente
  const [extraSconto, setExtraSconto] = useState('');
  const [extraScontoMotivo, setExtraScontoMotivo] = useState('');
  const [extraScontoRequestId, setExtraScontoRequestId] = useState(null);
  const [extraScontoRequests, setExtraScontoRequests] = useState([]); // tutte quelle relative a questa analisi
  const [reqDialogOpen, setReqDialogOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Sprint 40 - Workflow unificato "Genera proposta cliente"
  const [proposalDialogOpen, setProposalDialogOpen] = useState(false);
  const [proposalSubmitting, setProposalSubmitting] = useState(false);
  const [proposalForm, setProposalForm] = useState({
    email: '', telefono: '', trattamento_dati: false, rivendibilita_dati: false, marketing: false,
  });
  // Sprint 47 quinquies — Standalone "Genera lettera di accompagno"
  const [letterDialogOpen, setLetterDialogOpen] = useState(false);
  const [letterSubmitting, setLetterSubmitting] = useState(false);
  const [letterForm, setLetterForm] = useState({
    trattamento_dati: false, rivendibilita_dati: false, marketing: false,
    include_bolletta: true, include_doc_identita: false,
  });

  // Helper: calcola consumo annuo e prezzo medio quando non estratti da OCR.
  // Strategia: estrapolazione dal periodo (totale_kwh_periodo × 365 / giorni)
  // e dalle voci dettaglio (totale_lordo_eur × moltiplicatore / consumo_anno).
  const enrichWithComputedFallbacks = (extracted) => {
    if (!extracted) return extracted;
    const next = { ...extracted };
    const consumi = { ...(next.consumi || {}) };
    const periodo = next.periodo_fatturazione || {};
    const voci = next.voci_dettaglio || {};
    const giorni = parseFloat(periodo.giorni) || 30;
    const mult = giorni > 0 ? 365 / giorni : 12;

    const periodKwh = parseFloat(consumi.totale_kwh_periodo) || 0;
    const annoEsplicito = parseFloat(consumi.totale_kwh_anno) || 0;
    let consumoAnno = annoEsplicito;
    if (!consumoAnno && periodKwh) {
      consumoAnno = Math.round(periodKwh * mult);
      consumi.totale_kwh_anno = consumoAnno;
      consumi._anno_stimato = true;
      // Stesso scaling per fasce annue se mancano
      ['F1', 'F2', 'F3'].forEach(fascia => {
        const periodoFascia = parseFloat(consumi[`${fascia}_kwh`]) || 0;
        if (periodoFascia && !consumi[`${fascia}_kwh_anno`]) {
          consumi[`${fascia}_kwh_anno`] = Math.round(periodoFascia * mult);
        }
      });
    }
    next.consumi = consumi;

    // Totale annuo stimato in € (se mancante)
    let totaleAnnoEur = parseFloat(next.totale_annuo_stimato_eur) || 0;
    const totalePeriodoEur = parseFloat(voci.totale_lordo_eur) || 0;
    if (!totaleAnnoEur && totalePeriodoEur) {
      totaleAnnoEur = Math.round(totalePeriodoEur * mult * 100) / 100;
      next.totale_annuo_stimato_eur = totaleAnnoEur;
      next._totale_anno_stimato = true;
    }

    // Prezzo medio attuale €/kWh (se mancante)
    const prezzi = { ...(next.prezzi_attuali || {}) };
    let prezzoMedio = parseFloat(prezzi.prezzo_medio_kwh) || 0;
    if (!prezzoMedio && totaleAnnoEur && consumoAnno) {
      prezzoMedio = Math.round((totaleAnnoEur / consumoAnno) * 10000) / 10000;
      prezzi.prezzo_medio_kwh = prezzoMedio;
      prezzi.metodo_calcolo = 'stimato';
    }
    next.prezzi_attuali = prezzi;
    return next;
  };

  const fetchData = useCallback(async () => {
    try {
      const url = archiveView ? '/api/fatture/analyses?only_archived=true' : '/api/fatture/analyses';
      const [aRes, oRes] = await Promise.all([
        fetch(`${API}${url}`, { credentials: 'include' }),
        fetch(`${API}/api/offers?attiva=true`, { credentials: 'include' }),
      ]);
      if (aRes.ok) setAnalyses(await aRes.json());
      if (oRes.ok) setOffers(await oRes.json());
    } catch (e) { toast.error('Errore caricamento'); }
    finally { setLoading(false); }
  }, [archiveView]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const saveReview = async () => {
    // In fase analisi NON sono obbligatori contatti né consensi (sono
    // richiesti dopo, in fase creazione lead / firma). Validiamo SOLO il
    // formato base dell'email se presente.
    if (review.email && !review.email.includes('@')) return toast.error('Formato email non valido');

    try {
      const res = await fetch(`${API}/api/fatture/analyses/${active.id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_review: review, consensi }),
      });
      const data = await res.json();
      if (res.ok) { toast.success('Dati salvati'); setStep('simulate'); }
      else toast.error(data.detail || 'Errore');
    } catch (e) { toast.error('Errore'); }
  };

  const runSimulation = async () => {
    if (!selectedOfferId) return toast.error('Seleziona un\'offerta');
    try {
      const payload = { offer_id: selectedOfferId };
      // Extra sconto: solo se l'utente può applicarlo direttamente OPPURE
      // se sta riapplicando una richiesta già approvata.
      if (extraScontoRequestId) {
        payload.extra_sconto_request_id = extraScontoRequestId;
      } else if (canApplyExtraScontoDirectly) {
        const v = parseFloat(extraSconto || '0');
        if (v > 0) payload.extra_sconto_eur_anno = v;
      }
      const res = await fetch(`${API}/api/fatture/analyses/${active.id}/simulate`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok) { setSimulation(data); fetchData(); }
      else toast.error(data.detail || 'Errore simulazione');
    } catch (e) { toast.error('Errore'); }
  };

  const fetchExtraScontoRequests = useCallback(async (aid) => {
    if (!aid) { setExtraScontoRequests([]); return; }
    try {
      const res = await fetch(`${API}/api/extra-sconto/requests?analysis_id=${aid}`, { credentials: 'include' });
      if (res.ok) {
        const list = await res.json();
        setExtraScontoRequests(list);
        // Se c'è una richiesta APPROVED, pre-seleziono il suo importo
        const approved = list.find(r => r.status === 'approved');
        if (approved && !extraScontoRequestId) {
          setExtraSconto(String(approved.importo_approvato_eur_anno || 0));
          setExtraScontoRequestId(approved.id);
        }
      }
    } catch (e) { /* silent */ }
  }, []);

  const submitExtraScontoRequest = async () => {
    const importo = parseFloat(extraSconto || '0');
    if (!importo || importo <= 0) return toast.error('Inserisci un importo > 0');
    if ((extraScontoMotivo || '').trim().length < 5) return toast.error('Motivazione obbligatoria (min 5 caratteri)');
    if (!selectedOfferId) return toast.error('Seleziona prima un\'offerta');
    try {
      const res = await fetch(`${API}/api/extra-sconto/requests`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          analysis_id: active.id,
          offer_id: selectedOfferId,
          importo_richiesto_eur_anno: importo,
          motivo: extraScontoMotivo.trim(),
        }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Richiesta inviata. Riceverai notifica dopo l\'approvazione del tuo agente.');
        setReqDialogOpen(false);
        setExtraScontoMotivo('');
        fetchExtraScontoRequests(active.id);
      } else toast.error(data.detail || 'Errore invio richiesta');
    } catch (e) { toast.error('Errore'); }
  };

  // Sprint 46 - Manual override simulation (bypass calc bug)
  // Salvataggio diretto (legacy, mantenuto per i campi senza pending)
  const overrideSimField = async (field, value) => {
    if (!active) return;
    try {
      const v = parseFloat(value);
      if (Number.isNaN(v)) return;
      const r = await fetch(`${API}/api/fatture/analyses/${active.id}/simulation/override`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ overrides: { [field]: v } }),
      });
      const data = await r.json();
      if (r.ok) {
        setSimulation(data);
        toast.success(`${field} aggiornato`);
      } else toast.error(data.detail || 'Errore override');
    } catch (e) { toast.error('Errore'); }
  };

  // Sprint 47 quater — Modifiche pending: accumulo in state locale e applico
  // tutto in una sola PATCH quando il commerciale clicca "Applica modifiche".
  const [pendingOverrides, setPendingOverrides] = useState({});
  const [applying, setApplying] = useState(false);
  const setPending = (field, value) => {
    const v = parseFloat(String(value).replace(",", "."));
    if (Number.isNaN(v)) return;
    setPendingOverrides(prev => ({ ...prev, [field]: v }));
  };
  const hasPending = Object.keys(pendingOverrides).length > 0;
  const applyPendingOverrides = async () => {
    if (!active || !hasPending) return;
    setApplying(true);
    try {
      const r = await fetch(`${API}/api/fatture/analyses/${active.id}/simulation/override`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ overrides: pendingOverrides }),
      });
      const data = await r.json();
      if (r.ok) {
        setSimulation(data);
        setPendingOverrides({});
        toast.success(`${Object.keys(pendingOverrides).length} modifiche applicate`, { duration: 5000 });
      } else toast.error(data.detail || 'Errore applicazione modifiche');
    } catch (e) { toast.error(`Errore: ${e.message}`); }
    finally { setApplying(false); }
  };
  const cancelPendingOverrides = () => {
    setPendingOverrides({});
    toast.message('Modifiche annullate');
  };

  // Sprint 46 - Multi-CTE comparison
  const [compareOfferIds, setCompareOfferIds] = useState([]);
  const [compareResults, setCompareResults] = useState(null);
  const [comparing, setComparing] = useState(false);
  const toggleCompareOffer = (oid) => {
    setCompareOfferIds(arr => arr.includes(oid) ? arr.filter(x => x !== oid) : [...arr, oid]);
  };
  const runCompare = async () => {
    if (compareOfferIds.length < 2) { toast.error('Seleziona almeno 2 CTE'); return; }
    setComparing(true);
    try {
      const r = await fetch(`${API}/api/fatture/analyses/${active.id}/compare`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ offer_ids: compareOfferIds }),
      });
      const data = await r.json();
      if (r.ok) setCompareResults(data); else toast.error(data.detail || 'Errore confronto');
    } catch (e) { toast.error('Errore'); }
    finally { setComparing(false); }
  };
  const pickBestFromCompare = (oid) => {
    setSelectedOfferId(oid);
    setCompareResults(null);
    setCompareOfferIds([]);
    // Esegui simulazione per persistere
    setTimeout(() => runSimulation(), 100);
  };

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const params = archiveView ? '?only_archived=true' : '';
      const res = await fetch(`${API}/api/fatture/export-xlsx${params}`, { credentials: 'include' });
      if (!res.ok) {
        toast.error('Errore export Excel');
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `analisi_fatture_${Date.now()}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Excel scaricato');
    } catch (e) { toast.error('Errore export'); }
    finally { setExporting(false); }
  };

  const generateLetter = async () => {
    try {
      const res = await fetch(`${API}/api/fatture/analyses/${active.id}/lettera-pdf`, {
        method: 'POST', credentials: 'include',
      });
      if (!res.ok) {
        const d = await res.json();
        return toast.error(d.detail || 'Errore generazione');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      toast.success('Lettera comparativa generata');
      setStep('done');
    } catch (e) { toast.error('Errore'); }
  };

  const createLead = async () => {
    try {
      const res = await fetch(`${API}/api/fatture/analyses/${active.id}/create-lead`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.already_exists ? 'Lead aggiornato' : 'Lead creato');
        setStep('list');
        setActive(null);
        fetchData();
      } else toast.error(data.detail || 'Errore');
    } catch (e) { toast.error('Errore'); }
  };

  // Workflow unificato: 1) raccogli dati mancanti, 2) genera PDF, 3) crea lead con PDF allegato
  const openProposalDialog = () => {
    setProposalForm({
      email: review?.email || '',
      telefono: review?.telefono || '',
      trattamento_dati: !!consensi.trattamento_dati,
      rivendibilita_dati: !!consensi.rivendibilita_dati,
      marketing: !!consensi.marketing,
    });
    setProposalDialogOpen(true);
  };

  const submitProposalAndLead = async () => {
    // Validation: GDPR obbligatori + email valida
    if (!proposalForm.trattamento_dati) {
      return toast.error('Il consenso GDPR (trattamento dati) è obbligatorio per generare la lettera');
    }
    if (proposalForm.email && !proposalForm.email.includes('@')) {
      return toast.error('Email non valida');
    }
    if (!proposalForm.email && !proposalForm.telefono) {
      return toast.error('Inserisci almeno email o telefono per creare il lead');
    }
    setProposalSubmitting(true);
    try {
      // 1. Salva email/tel/consensi sull'analisi
      const updRes = await fetch(`${API}/api/fatture/analyses/${active.id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_review: { ...review, email: proposalForm.email, telefono: proposalForm.telefono },
          consensi: {
            trattamento_dati: proposalForm.trattamento_dati,
            rivendibilita_dati: proposalForm.rivendibilita_dati,
            marketing: proposalForm.marketing,
          },
        }),
      });
      if (!updRes.ok) {
        const e = await updRes.json().catch(() => ({}));
        throw new Error(e.detail || 'Errore salvataggio dati');
      }
      // 2. Genera lettera PDF (e la apre nel browser per download immediato)
      const pdfRes = await fetch(`${API}/api/fatture/analyses/${active.id}/lettera-pdf`, {
        method: 'POST', credentials: 'include',
      });
      if (!pdfRes.ok) {
        const e = await pdfRes.json().catch(() => ({}));
        throw new Error(e.detail || 'Errore generazione PDF');
      }
      const blob = await pdfRes.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');

      // 3. Crea/aggiorna lead (la lettera è già linkata via fattura_analyses.lettera_pdf_path)
      const leadRes = await fetch(`${API}/api/fatture/analyses/${active.id}/create-lead`, {
        method: 'POST', credentials: 'include',
      });
      if (!leadRes.ok) {
        const e = await leadRes.json().catch(() => ({}));
        throw new Error(e.detail || 'Errore creazione lead');
      }
      const leadData = await leadRes.json();
      toast.success(
        leadData.already_exists
          ? 'Lead aggiornato + Lettera PDF allegata'
          : 'Lead creato + Lettera PDF allegata',
        { duration: 5000 },
      );
      setProposalDialogOpen(false);
      setStep('list');
      setActive(null);
      fetchData();
    } catch (e) {
      toast.error(e.message || 'Errore nel completamento della proposta', { duration: 6000 });
    } finally {
      setProposalSubmitting(false);
    }
  };

  // Sprint 47 quinquies — Workflow leggero: genera SOLO la lettera di accompagno (senza creare lead).
  // Include opzionalmente la bolletta originale e il documento di identità in download multipli.
  const openLetterOnlyDialog = () => {
    setLetterForm({
      trattamento_dati: !!consensi.trattamento_dati,
      rivendibilita_dati: !!consensi.rivendibilita_dati,
      marketing: !!consensi.marketing,
      include_bolletta: true,
      include_doc_identita: false,
    });
    setLetterDialogOpen(true);
  };

  const submitLetterOnly = async () => {
    if (!letterForm.trattamento_dati) {
      return toast.error('Il consenso GDPR (trattamento dati) è obbligatorio per generare la lettera');
    }
    setLetterSubmitting(true);
    try {
      // 1. Salva consensi sull'analisi (richiesto dal backend)
      const updRes = await fetch(`${API}/api/fatture/analyses/${active.id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          consensi: {
            trattamento_dati: letterForm.trattamento_dati,
            rivendibilita_dati: letterForm.rivendibilita_dati,
            marketing: letterForm.marketing,
          },
        }),
      });
      if (!updRes.ok) {
        const e = await updRes.json().catch(() => ({}));
        throw new Error(e.detail || 'Errore salvataggio consensi');
      }
      // 2. Genera la lettera PDF
      const params = new URLSearchParams();
      if (letterForm.include_bolletta) params.set('include_bolletta', '1');
      if (letterForm.include_doc_identita) params.set('include_doc_identita', '1');
      const qs = params.toString() ? `?${params}` : '';
      const pdfRes = await fetch(`${API}/api/fatture/analyses/${active.id}/lettera-pdf${qs}`, {
        method: 'POST', credentials: 'include',
      });
      if (!pdfRes.ok) {
        const e = await pdfRes.json().catch(() => ({}));
        throw new Error(e.detail || 'Errore generazione PDF');
      }
      const blob = await pdfRes.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');

      // 3. Scarica anche gli allegati richiesti (bolletta originale, doc identità) in tab separati
      if (letterForm.include_bolletta) {
        try {
          const billRes = await fetch(`${API}/api/fatture/analyses/${active.id}/original-pdf`, {
            credentials: 'include',
          });
          if (billRes.ok) {
            const b = await billRes.blob();
            window.open(URL.createObjectURL(b), '_blank');
          }
        } catch (e) { /* non-fatal */ }
      }
      if (letterForm.include_doc_identita) {
        try {
          const docRes = await fetch(`${API}/api/fatture/analyses/${active.id}/lead-identity-doc`, {
            credentials: 'include',
          });
          if (docRes.ok) {
            const b = await docRes.blob();
            window.open(URL.createObjectURL(b), '_blank');
          } else {
            toast.message('Documento d\'identità non trovato sul lead collegato');
          }
        } catch (e) { /* non-fatal */ }
      }
      toast.success('Lettera di accompagno generata' + (letterForm.include_bolletta ? ' + bolletta in allegato' : ''), { duration: 5000 });
      setLetterDialogOpen(false);
    } catch (e) {
      toast.error(e.message || 'Errore generazione lettera', { duration: 6000 });
    } finally {
      setLetterSubmitting(false);
    }
  };

  const reopen = async (a) => {
    setActive(a);
    const baseReview = a.user_review || a.extracted || {};
    const enriched = enrichWithComputedFallbacks(baseReview);
    setReview({ ...enriched, email: a.user_review?.email || '', telefono: a.user_review?.telefono || '' });
    setConsensi(a.consensi || { trattamento_dati: false, rivendibilita_dati: false, marketing: false });
    setSelectedOfferId(a.selected_offer_id || '');
    setSimulation(a.simulation || null);
    setExtraSconto(a.simulation?.extra_sconto_eur_anno ? String(a.simulation.extra_sconto_eur_anno) : '');
    setExtraScontoRequestId(a.simulation?.extra_sconto_request_id || null);
    setExtraScontoMotivo('');
    await fetchExtraScontoRequests(a.id);
    if (a.simulation) setStep('simulate');
    else if (a.user_review) setStep('simulate');
    else setStep('review');
  };

  // Sprint 47: avvia un singolo job OCR async e ritorna il job_id (o null in caso di errore).
  const startOcrJob = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API}/api/fatture/ocr-analyze-async`, {
      method: 'POST', credentials: 'include', body: fd,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      if (res.status === 401) throw new Error('Sessione scaduta — rifai login');
      if (res.status === 402) throw new Error(data.detail || 'Budget Universal Key esaurito');
      throw new Error(data.detail || `HTTP ${res.status}`);
    }
    return data.job_id;
  };

  // Sprint 47: upload multi-file. Avvia tutti i job in parallelo, poi polling unificato.
  const handleUploadMulti = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    // crea voci coda; status iniziale 'queued'
    const initialQueue = files.map((f, idx) => ({
      id: `${Date.now()}-${idx}`,
      filename: f.name,
      status: 'queued',
      jobId: null,
      error: null,
    }));
    setUploadQueue(prev => [...initialQueue, ...prev]);

    // POST tutti i file in parallelo (max 3 concorrenti per non saturare il backend)
    const queueIds = initialQueue.map(q => q.id);
    const concurrency = 3;
    let i = 0;
    const submitNext = async () => {
      while (i < files.length) {
        const idx = i++;
        const qid = queueIds[idx];
        try {
          const jobId = await startOcrJob(files[idx]);
          setUploadQueue(prev => prev.map(q => q.id === qid ? { ...q, jobId, status: 'processing' } : q));
        } catch (e) {
          setUploadQueue(prev => prev.map(q => q.id === qid ? { ...q, status: 'failed', error: e.message } : q));
        }
      }
    };
    await Promise.all(Array.from({ length: concurrency }, submitNext));
    setUploading(false);
    toast.message(`${files.length} fattur${files.length === 1 ? 'a' : 'e'} in coda di analisi`, { duration: 4000 });
    // Sprint 47 sexies: il polling è ora declarative (useEffect su hasPendingJobs).
    // L'aggiornamento di uploadQueue qui sopra trigger automaticamente il useEffect.
  };

  // Wrapper single-file (manteneuto per compatibilità — usato anche da Drag&Drop futuro).
  const handleUpload = async (file) => {
    await handleUploadMulti([file]);
  };

  // Sprint 47 sexies — Polling DECLARATIVE su tutti i job OCR in coda.
  // Bug-fix rispetto al precedente "imperative" startPollingIfNeeded:
  //   - L'effect è guidato da hasPendingJobs (calcolato sullo state attuale).
  //   - Quando hasPendingJobs=true → l'intervallo parte (e fa subito un poll).
  //   - Quando hasPendingJobs=false → cleanup automatico dell'intervallo.
  //   - Niente race condition tra queueRef.current e setUploadQueue: la
  //     funzione poll legge da queueRef, ma è ricreata ogni volta che
  //     l'effect ri-parte (e l'effect ri-parte appena hasPendingJobs cambia).
  const queueRef = useRef([]);
  const pollIntervalRef = useRef(null);
  const lastNotifiedRef = useRef({}); // {jobId: 'done'|'failed'} per evitare toast duplicati

  // Mantieni queueRef sincronizzato con lo state (per closure-safe access).
  useEffect(() => { queueRef.current = uploadQueue; }, [uploadQueue]);

  // Persistenza coda in localStorage (sopravvive a refresh / navigazione).
  useEffect(() => {
    try {
      // Salvo solo job ancora attivi (non i 'done' vecchi).
      const toSave = uploadQueue.filter(q => q.status !== 'done');
      if (toSave.length > 0) {
        localStorage.setItem('ocrQueue', JSON.stringify(toSave));
      } else if (uploadQueue.length > 0) {
        // Tutto completato → pulisco localStorage per non re-idratare
        localStorage.removeItem('ocrQueue');
      }
    } catch (e) { /* quota */ }
  }, [uploadQueue]);

  // Booleano: ci sono job ancora pending? Triggera il useEffect del polling.
  const hasPendingJobs = uploadQueue.some(
    q => q.jobId && (q.status === 'processing' || q.status === 'queued'),
  );

  useEffect(() => {
    if (!hasPendingJobs) {
      // Niente da pollare → cleanup garantito
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      return;
    }

    const poll = async () => {
      // Leggo sempre la coda corrente (ref aggiornato dall'effect su uploadQueue)
      const pending = queueRef.current.filter(
        q => q.jobId && (q.status === 'processing' || q.status === 'queued'),
      );
      if (pending.length === 0) return; // nessun job pending in questo tick — l'effect chiuderà l'intervallo al prossimo re-render

      const updates = await Promise.all(pending.map(async (q) => {
        try {
          const r = await fetch(`${API}/api/fatture/ocr-jobs/${q.jobId}`, { credentials: 'include' });
          if (r.status === 404) {
            // Job sparito dal DB (es. retention scaduta) — marchiamo come failed così l'utente vede il problema
            return { id: q.id, jobId: q.jobId, status: 'failed', error: 'Job non trovato (probabilmente scaduto)' };
          }
          if (r.status === 401) {
            // Sessione scaduta — interceptor fa refresh in automatico; saltiamo questo tick
            return null;
          }
          if (!r.ok) return null;
          const d = await r.json();
          return { id: q.id, jobId: q.jobId, status: d.status, error: d.error, analysisId: d.analysis_id };
        } catch (e) {
          // Errore di rete transitorio — non blocchiamo il polling
          return null;
        }
      }));

      // Notifiche per ogni nuovo done/failed (idempotente via lastNotifiedRef).
      updates.forEach(u => {
        if (!u) return;
        const prev = queueRef.current.find(q => q.id === u.id);
        if (!prev) return;
        const alreadyNotified = lastNotifiedRef.current[u.jobId];
        if (u.status === 'done' && alreadyNotified !== 'done') {
          lastNotifiedRef.current[u.jobId] = 'done';
          toast.success(`✓ Fattura "${prev.filename}" analizzata con successo`, {
            duration: 7000,
            action: u.analysisId ? {
              label: 'Apri',
              onClick: () => {
                fetch(`${API}/api/fatture/analyses/${u.analysisId}`, { credentials: 'include' })
                  .then(r => r.json()).then(a => {
                    if (a && a.id) {
                      setActive({ ...a, id: a.id });
                      const enriched = enrichWithComputedFallbacks(a.extracted || {});
                      setReview({ ...enriched, email: a.user_review?.email || '', telefono: a.user_review?.telefono || '' });
                      setConsensi({ trattamento_dati: false, rivendibilita_dati: false, marketing: false });
                      setSelectedOfferId(a.selected_offer_id || '');
                      setSimulation(a.simulation || null);
                      setStep('review');
                    }
                  });
              },
            } : undefined,
          });
        }
        if (u.status === 'failed' && alreadyNotified !== 'failed') {
          lastNotifiedRef.current[u.jobId] = 'failed';
          toast.error(`✗ "${prev.filename}": ${u.error || 'analisi fallita'}`, { duration: 12000 });
        }
      });

      // Aggiorna state coda. Quando tutti completano, hasPendingJobs diventa false → useEffect cleanup
      setUploadQueue(prev => prev.map(item => {
        const u = updates.find(x => x && x.id === item.id);
        if (!u) return item;
        return { ...item, status: u.status || item.status, error: u.error || item.error };
      }));

      // Refresh lista analisi se almeno un job è completato.
      const anyDone = updates.some(u => u && (u.status === 'done' || u.status === 'failed'));
      if (anyDone) fetchData();
    };

    // Run immediato + intervallo
    poll();
    pollIntervalRef.current = setInterval(poll, 3000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [hasPendingJobs, fetchData]);

  // Carica coda da localStorage al mount (sopravvive a refresh/navigazione).
  // Il polling parte automaticamente via il useEffect declarative sopra
  // appena setUploadQueue popola lo stato.
  useEffect(() => {
    try {
      const saved = localStorage.getItem('ocrQueue');
      if (!saved) return;
      const arr = JSON.parse(saved);
      if (!Array.isArray(arr) || arr.length === 0) return;
      // Filtra solo voci recenti (< 2 ore) per evitare di mostrare coda vecchia.
      const TWO_HOURS = 2 * 60 * 60 * 1000;
      const recent = arr.filter(item => {
        const ts = parseInt((item.id || '').split('-')[0], 10);
        return ts && (Date.now() - ts < TWO_HOURS) && item.jobId;
      });
      if (recent.length === 0) {
        localStorage.removeItem('ocrQueue');
        return;
      }
      setUploadQueue(recent);
    } catch (e) { /* corrupted */ }
    // Nota: niente cleanup qui — il polling è gestito dall'altro useEffect
  }, []);  // run-once on mount

  // RIMOSSO il vecchio approccio imperativo startPollingIfNeeded/stopPolling.

  const clearDoneFromQueue = () => {
    setUploadQueue(prev => prev.filter(q => q.status !== 'done'));
  };

  // Sprint 47 septies — pulizia voci failed (per sbloccare la UI quando
  // un job è morto lato backend, es. Gemini hang con timeout).
  const clearFailedFromQueue = () => {
    setUploadQueue(prev => prev.filter(q => q.status !== 'failed'));
  };

  // Sprint 47 - bulk archive/restore analisi.
  const toggleAnalysisSelect = (aid) => {
    setSelectedAnalyses(prev => {
      const next = new Set(prev);
      if (next.has(aid)) next.delete(aid); else next.add(aid);
      return next;
    });
  };
  const toggleAllAnalyses = () => {
    if (selectedAnalyses.size === analyses.length) setSelectedAnalyses(new Set());
    else setSelectedAnalyses(new Set(analyses.map(a => a.id)));
  };
  const bulkArchiveAnalyses = async () => {
    if (!selectedAnalyses.size) return;
    const ids = [...selectedAnalyses];
    if (!window.confirm(`Archiviare ${ids.length} analisi selezionate?`)) return;
    try {
      const res = await fetch(`${API}/api/fatture/analyses/bulk-archive`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ analysis_ids: ids, reason: 'bulk_manuale' }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(`${d.archived_count} analisi archiviate`);
        setSelectedAnalyses(new Set());
        fetchData();
      } else toast.error(d.detail || 'Errore archiviazione');
    } catch (e) { toast.error(`Errore: ${e.message}`); }
  };
  const bulkRestoreAnalyses = async () => {
    if (!selectedAnalyses.size) return;
    const ids = [...selectedAnalyses];
    try {
      const res = await fetch(`${API}/api/fatture/analyses/bulk-restore`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ analysis_ids: ids }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(`${d.restored_count} analisi ripristinate`);
        setSelectedAnalyses(new Set());
        fetchData();
      } else toast.error(d.detail || 'Errore ripristino');
    } catch (e) { toast.error(`Errore: ${e.message}`); }
  };

  const archiveAnalysis = async (e, aid) => {
    e.stopPropagation();
    if (!window.confirm('Archiviare questa analisi? Potrà essere ripristinata in seguito.')) return;
    try {
      const res = await fetch(`${API}/api/fatture/analyses/${aid}/archive`, {
        method: 'PATCH', credentials: 'include',
      });
      if (res.ok) { toast.success('Analisi archiviata'); fetchData(); }
      else toast.error('Errore archiviazione');
    } catch (err) { toast.error('Errore archiviazione'); }
  };

  const restoreAnalysis = async (e, aid) => {
    e.stopPropagation();
    try {
      const res = await fetch(`${API}/api/fatture/analyses/${aid}/restore`, {
        method: 'PATCH', credentials: 'include',
      });
      if (res.ok) { toast.success('Analisi ripristinata'); fetchData(); }
      else toast.error('Errore ripristino');
    } catch (err) { toast.error('Errore ripristino'); }
  };

  const openOriginalPdf = (e, aid) => {
    e.stopPropagation();
    window.open(`${API}/api/fatture/analyses/${aid}/original-pdf`, '_blank');
  };

  const updateReview = (path, value) => {
    setReview(prev => {
      const next = { ...prev };
      const keys = path.split('.');
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!obj[keys[i]]) obj[keys[i]] = {};
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = value;
      return next;
    });
  };

  if (loading) return <div className="p-8 text-slate-500">Caricamento...</div>;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="fatture-ocr-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Receipt size={32} weight="duotone" className="text-orange-500" />
            Analisi Fatture (OCR)
          </h1>
          <p className="text-slate-500 mt-1">
            Carica una bolletta, l&apos;AI Gemini estrae i dati, calcola il risparmio con la nostra offerta e genera la lettera comparativa.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <Button onClick={exportXlsx} disabled={exporting} variant="outline" data-testid="export-xlsx-btn">
            <Download size={16} className="mr-2" /> {exporting ? 'Export…' : 'Esporta Excel'}
          </Button>
          <input id="upload-fattura" type="file" accept=".pdf,.png,.jpg,.jpeg,.webp"
                 multiple
                 onChange={(e) => {
                   const fls = Array.from(e.target.files || []);
                   if (fls.length) handleUploadMulti(fls);
                   e.target.value = ''; // reset so re-select stesso file funziona
                 }}
                 disabled={uploading} className="hidden" data-testid="upload-fattura-input" />
          <Button onClick={() => document.getElementById('upload-fattura').click()}
                  disabled={uploading} className="bg-orange-500 hover:bg-orange-600"
                  data-testid="upload-fattura-btn">
            {analyzing ? <><Sparkle size={18} className="mr-2 animate-pulse" /> AI in lavoro...</>
                       : <><Upload size={18} className="mr-2" /> Carica fatture</>}
          </Button>
        </div>
      </div>

      {/* LIST */}
      {step === 'list' && (
        <div>
          {/* Sprint 47 — Pannello Coda OCR (upload multi-file) */}
          {uploadQueue.length > 0 && (
            <div className="mb-4 bg-white border rounded-lg p-3" data-testid="ocr-queue-panel">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  <Sparkle size={16} className="text-orange-500" weight="fill" />
                  Coda analisi OCR
                  <Badge variant="outline" className="text-[10px]">
                    {uploadQueue.filter(q => q.status === 'done').length}/{uploadQueue.length} completate
                  </Badge>
                </div>
                {uploadQueue.some(q => q.status === 'done') && (
                  <button onClick={clearDoneFromQueue}
                          className="text-xs text-slate-500 hover:text-slate-700"
                          data-testid="clear-done-queue-btn">
                    Rimuovi completate
                  </button>
                )}
                {uploadQueue.some(q => q.status === 'failed') && (
                  <button onClick={clearFailedFromQueue}
                          className="text-xs text-red-500 hover:text-red-700 ml-3"
                          data-testid="clear-failed-queue-btn">
                    Pulisci errori
                  </button>
                )}
              </div>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {uploadQueue.map(q => (
                  <div key={q.id} className="flex items-start gap-2 text-xs">
                    <div className="flex-1 min-w-0">
                      <div className="truncate font-medium text-slate-700">{q.filename}</div>
                      {q.status === 'failed' && q.error && (
                        <div className="text-red-600 text-[10px] mt-0.5 truncate" title={q.error}>
                          {q.error}
                        </div>
                      )}
                    </div>
                    {q.status === 'queued' && <Badge className="bg-slate-100 text-slate-600">In coda</Badge>}
                    {q.status === 'processing' && (
                      <Badge className="bg-blue-100 text-blue-700 inline-flex items-center gap-1">
                        <Sparkle size={10} className="animate-pulse" /> Analisi…
                      </Badge>
                    )}
                    {q.status === 'done' && <Badge className="bg-green-100 text-green-700">✓ Pronta</Badge>}
                    {q.status === 'failed' && (
                      <Badge className="bg-red-100 text-red-700" title={q.error}>
                        ✗ Errore
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <button onClick={() => { setArchiveView(false); setSelectedAnalyses(new Set()); }}
                      className={`text-xs px-3 py-1.5 rounded-md font-medium ${!archiveView ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                      data-testid="tab-active-analyses">
                📂 Attive ({!archiveView ? analyses.length : '·'})
              </button>
              <button onClick={() => { setArchiveView(true); setSelectedAnalyses(new Set()); }}
                      className={`text-xs px-3 py-1.5 rounded-md font-medium ${archiveView ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                      data-testid="tab-archived-analyses">
                🗄️ Archiviate ({archiveView ? analyses.length : '·'})
              </button>
              {analyses.length > 0 && (
                <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 ml-3 cursor-pointer">
                  <input type="checkbox"
                         checked={selectedAnalyses.size === analyses.length && analyses.length > 0}
                         onChange={toggleAllAnalyses}
                         className="accent-orange-500"
                         data-testid="select-all-analyses" />
                  Seleziona tutto
                </label>
              )}
            </div>
            <span className="text-xs text-slate-400">Auto-archiviazione: fatture &gt; 30 giorni (configurabile in Impostazioni)</span>
          </div>

          {/* Sprint 47 — Toolbar bulk archive/restore analisi */}
          {selectedAnalyses.size > 0 && (
            <div className="mb-3 bg-orange-50 border border-orange-200 rounded-lg p-2 flex items-center gap-3"
                 data-testid="analyses-bulk-toolbar">
              <span className="text-xs font-medium text-orange-800">{selectedAnalyses.size} selezionate:</span>
              {archiveView ? (
                <button onClick={bulkRestoreAnalyses}
                        className="px-2.5 py-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded font-medium"
                        data-testid="bulk-restore-analyses-btn">
                  ↩ Ripristina selezionate
                </button>
              ) : (
                <button onClick={bulkArchiveAnalyses}
                        className="px-2.5 py-1 text-xs bg-slate-700 hover:bg-slate-800 text-white rounded font-medium"
                        data-testid="bulk-archive-analyses-btn">
                  🗄️ Archivia selezionate
                </button>
              )}
              <button onClick={() => setSelectedAnalyses(new Set())}
                      className="px-2.5 py-1 text-xs text-slate-500 hover:text-slate-700 hover:bg-white rounded ml-auto"
                      data-testid="analyses-deselect-all">
                Deseleziona
              </button>
            </div>
          )}
          {analyses.length === 0 ? (
            <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
              <Receipt size={64} className="mx-auto text-slate-300 mb-4" weight="duotone" />
              <p className="text-slate-500 mb-2">
                {archiveView ? 'Nessuna analisi archiviata.' : 'Nessuna analisi. Carica la prima bolletta per iniziare.'}
              </p>
              <p className="text-xs text-slate-400">Formati supportati: PDF, PNG, JPG · Max 15MB</p>
            </div>
          ) : (
            <div className="grid gap-3">
              {analyses.map(a => {
                const data = a.user_review || a.extracted || {};
                const sim = a.simulation;
                return (
                  <div key={a.id} className={`bg-white rounded-lg border p-4 hover:shadow-sm transition cursor-pointer ${a.archived ? 'opacity-70 border-slate-300' : ''} ${selectedAnalyses.has(a.id) ? 'ring-2 ring-orange-300 bg-orange-50/40' : ''}`}
                       onClick={() => reopen(a)} data-testid={`analysis-${a.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <input type="checkbox"
                             checked={selectedAnalyses.has(a.id)}
                             onChange={(e) => { e.stopPropagation(); toggleAnalysisSelect(a.id); }}
                             onClick={(e) => e.stopPropagation()}
                             className="accent-orange-500 mt-1 shrink-0"
                             data-testid={`select-analysis-${a.id}`} />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-slate-900">{data.ragione_sociale || a.filename}</h3>
                          {a.archived && <Badge className="bg-slate-200 text-slate-700">🗄️ Archiviata</Badge>}
                          {data.fornitore_attuale && <Badge variant="outline">Da: {data.fornitore_attuale}</Badge>}
                          {data.tipologia && <Badge>{data.tipologia.toUpperCase()}</Badge>}
                          {a.lead_id && <Badge className="bg-blue-100 text-blue-700">Lead creato</Badge>}
                          {sim && <Badge className="bg-green-100 text-green-700">Simulato</Badge>}
                          {a.lettera_pdf_path && <Badge className="bg-purple-100 text-purple-700">PDF</Badge>}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                          {data.pod && `POD: ${data.pod} · `}
                          {data.pdr && `PDR: ${data.pdr} · `}
                          Caricato: {new Date(a.uploaded_at).toLocaleString('it-IT')}
                        </div>
                        {sim && (
                          <div className="text-sm mt-2">
                            Attuale: <strong>{sim.totale_annuo_attuale?.toFixed(2)}€</strong>
                            <ArrowRight size={12} className="inline mx-1" />
                            Nostro: <strong>{sim.totale_annuo_nuovo?.toFixed(2)}€</strong>
                            <span className={`ml-2 font-bold ${sim.risparmio_annuo_eur > 0 ? 'text-green-700' : 'text-red-700'}`}>
                              {sim.risparmio_annuo_eur > 0 ? '−' : '+'}{Math.abs(sim.risparmio_annuo_eur).toFixed(2)}€/anno
                              ({Math.abs(sim.risparmio_pct).toFixed(1)}%)
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-1.5 shrink-0">
                        {a.original_file_path && (
                          <button onClick={(e) => openOriginalPdf(e, a.id)}
                                  title="Apri PDF originale della fattura"
                                  className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-orange-600"
                                  data-testid={`open-original-${a.id}`}>
                            <Eye size={16} />
                          </button>
                        )}
                        {a.archived ? (
                          <button onClick={(e) => restoreAnalysis(e, a.id)}
                                  title="Ripristina dall'archivio"
                                  className="p-1.5 rounded hover:bg-green-50 text-slate-500 hover:text-green-700"
                                  data-testid={`restore-${a.id}`}>
                            <ArrowCounterClockwise size={16} />
                          </button>
                        ) : (
                          <button onClick={(e) => archiveAnalysis(e, a.id)}
                                  title="Archivia (recuperabile)"
                                  className="p-1.5 rounded hover:bg-amber-50 text-slate-500 hover:text-amber-700"
                                  data-testid={`archive-${a.id}`}>
                            <Archive size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* REVIEW step */}
      <Dialog open={step === 'review' && !!active} onOpenChange={(o) => { if (!o) { setStep('list'); setActive(null); } }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle><Sparkle className="inline text-orange-500" weight="duotone" /> Verifica dati estratti</DialogTitle></DialogHeader>
          {review && (
            <div className="space-y-4">
              <p className="text-sm text-slate-500">Gemini AI ha estratto questi dati. Verifica e correggi se necessario.</p>

              <section className="border rounded-lg p-3 bg-slate-50">
                <h4 className="font-semibold mb-2">Anagrafica</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Ragione sociale</Label>
                    <Input value={review.ragione_sociale || ''} onChange={(e) => updateReview('ragione_sociale', e.target.value)} /></div>
                  <div><Label>Fornitore attuale</Label>
                    <Input value={review.fornitore_attuale || ''} onChange={(e) => updateReview('fornitore_attuale', e.target.value)} /></div>
                  <div><Label>P.IVA</Label>
                    <Input value={review.partita_iva || ''} onChange={(e) => updateReview('partita_iva', e.target.value)} /></div>
                  <div><Label>Codice Fiscale</Label>
                    <Input value={review.codice_fiscale || ''} onChange={(e) => updateReview('codice_fiscale', e.target.value)} /></div>
                  <div><Label>POD</Label>
                    <Input value={review.pod || ''} onChange={(e) => updateReview('pod', e.target.value)} /></div>
                  <div><Label>PDR</Label>
                    <Input value={review.pdr || ''} onChange={(e) => updateReview('pdr', e.target.value)} /></div>
                  <div className="col-span-2"><Label>Indirizzo fornitura</Label>
                    <Input value={review.indirizzo_fornitura || ''} onChange={(e) => updateReview('indirizzo_fornitura', e.target.value)} /></div>
                </div>
              </section>

              <section className="border rounded-lg p-3 bg-slate-50">
                <h4 className="font-semibold mb-2">Consumi e prezzi attuali</h4>
                <p className="text-xs text-slate-500 mb-2">
                  Editabili per <strong>correggere eventuali errori OCR</strong>. Se la bolletta è mensile/bimestrale e non riporta un consumo annuo,
                  abbiamo già <strong>estrapolato</strong> i valori dal periodo (×12 mensile / ×6 bimestrale).
                </p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>Consumo annuo (kWh) {review.consumi?._anno_stimato && <Badge variant="outline" className="ml-1 text-[10px] py-0 border-amber-300 text-amber-700">📐 stimato</Badge>}</Label>
                    <Input type="number" value={review.consumi?.totale_kwh_anno || 0}
                           onChange={(e) => updateReview('consumi.totale_kwh_anno', parseFloat(e.target.value) || 0)} /></div>
                  <div>
                    <Label>Prezzo medio attuale €/kWh {review.prezzi_attuali?.metodo_calcolo === 'stimato' && <Badge variant="outline" className="ml-1 text-[10px] py-0 border-amber-300 text-amber-700">📐 stimato</Badge>}</Label>
                    <Input type="number" step="0.0001" value={review.prezzi_attuali?.prezzo_medio_kwh || 0}
                           onChange={(e) => updateReview('prezzi_attuali.prezzo_medio_kwh', parseFloat(e.target.value) || 0)} /></div>
                  <div>
                    <Label>Totale annuo € {review._totale_anno_stimato && <Badge variant="outline" className="ml-1 text-[10px] py-0 border-amber-300 text-amber-700">📐 stimato</Badge>}</Label>
                    <Input type="number" step="0.01" value={review.totale_annuo_stimato_eur || 0}
                           onChange={(e) => updateReview('totale_annuo_stimato_eur', parseFloat(e.target.value) || 0)} /></div>
                </div>
              </section>

              <section className="border rounded-lg p-3 bg-slate-50/60">
                <h4 className="font-semibold mb-2 text-slate-700">📞 Contatti <span className="text-xs font-normal text-slate-500">(facoltativi in questa fase, da completare alla creazione lead)</span></h4>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Email</Label>
                    <Input type="email" value={review.email || ''} onChange={(e) => updateReview('email', e.target.value)} data-testid="review-email" /></div>
                  <div><Label>Telefono</Label>
                    <Input type="tel" value={review.telefono || ''} onChange={(e) => updateReview('telefono', e.target.value)} data-testid="review-telefono" /></div>
                </div>
              </section>

              <section className="border border-slate-200 rounded-lg p-4 bg-white">
                <h4 className="font-semibold mb-3 text-slate-700">🔒 Consensi GDPR <span className="text-xs font-normal text-slate-500">(facoltativi qui — diventano obbligatori in fase firma contratto)</span></h4>
                <div className="space-y-3">
                  <label className="flex items-start gap-3 cursor-pointer text-sm">
                    <Checkbox checked={consensi.trattamento_dati}
                              onCheckedChange={(v) => setConsensi({ ...consensi, trattamento_dati: !!v })}
                              data-testid="consenso-trattamento" className="mt-1" />
                    <span><strong>Consenso al trattamento dei dati personali</strong> (GDPR art. 6) per la finalità di analisi tecnico-economica della fornitura attuale ed elaborazione della proposta commerciale personalizzata.</span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer text-sm">
                    <Checkbox checked={consensi.rivendibilita_dati}
                              onCheckedChange={(v) => setConsensi({ ...consensi, rivendibilita_dati: !!v })}
                              data-testid="consenso-rivendibilita" className="mt-1" />
                    <span><strong>Consenso alla comunicazione dei dati a terzi e rivendibilità</strong> per attività commerciali e di mediazione.</span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer text-sm">
                    <Checkbox checked={consensi.marketing}
                              onCheckedChange={(v) => setConsensi({ ...consensi, marketing: !!v })} className="mt-1" />
                    <span>Consenso a ricevere comunicazioni commerciali e di marketing.</span>
                  </label>
                </div>
              </section>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setStep('list'); setActive(null); }}>Annulla</Button>
            <Button onClick={saveReview} className="bg-orange-500 hover:bg-orange-600" data-testid="save-review-btn">
              Salva e prosegui <ArrowRight size={14} className="ml-1" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SIMULATE step */}
      <Dialog open={step === 'simulate' && !!active} onOpenChange={(o) => { if (!o) { setStep('list'); setActive(null); } }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle><ChartLineUp className="inline text-orange-500" /> Simulazione risparmio</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="border rounded-lg p-3 bg-slate-50">
              <Label>Seleziona offerta da confrontare</Label>
              <Select value={selectedOfferId} onValueChange={setSelectedOfferId}>
                <SelectTrigger data-testid="offer-select"><SelectValue placeholder="Scegli un'offerta..." /></SelectTrigger>
                <SelectContent>
                  {offers.map(o => (
                    <SelectItem key={o.id} value={o.id}>
                      {o.nome_offerta} ({o.tipologia.toUpperCase()} · {o.tipo_prezzo})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={runSimulation} className="mt-3 bg-orange-500 hover:bg-orange-600"
                      disabled={!selectedOfferId} data-testid="run-simulation-btn">
                <ChartLineUp size={16} className="mr-2" /> Calcola risparmio
              </Button>

              {/* Sprint 46 - Multi-CTE comparison */}
              <div className="mt-4 pt-3 border-t border-slate-300">
                <div className="text-xs font-semibold text-slate-700 mb-2">🔄 Oppure confronta 2-5 CTE contemporaneamente:</div>
                <div className="grid grid-cols-2 gap-1 max-h-32 overflow-y-auto">
                  {offers.map(o => (
                    <label key={o.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-slate-100 p-1 rounded">
                      <input type="checkbox" checked={compareOfferIds.includes(o.id)}
                             onChange={() => toggleCompareOffer(o.id)}
                             data-testid={`compare-checkbox-${o.id}`} />
                      <span className="truncate">{o.nome_offerta}</span>
                    </label>
                  ))}
                </div>
                <Button size="sm" onClick={runCompare} disabled={comparing || compareOfferIds.length < 2}
                        className="mt-2 bg-purple-500 hover:bg-purple-600 text-white"
                        data-testid="run-compare-btn">
                  {comparing ? 'Confronto…' : `Confronta ${compareOfferIds.length} CTE`}
                </Button>
              </div>
            </div>

            {/* Risultati comparazione */}
            {compareResults && compareResults.results && compareResults.results.length > 0 && (
              <div className="border-2 border-purple-300 rounded-lg p-4 bg-purple-50" data-testid="compare-results">
                <div className="flex justify-between mb-3">
                  <h4 className="font-bold text-purple-800">Confronto CTE — ordine per risparmio decrescente</h4>
                  <button onClick={() => setCompareResults(null)} className="text-xs text-purple-600 hover:underline">Chiudi</button>
                </div>
                <table className="w-full text-sm">
                  <thead className="text-xs text-slate-600 border-b border-purple-300">
                    <tr><th className="text-left py-1">CTE</th><th className="text-right">Attuale</th><th className="text-right">Nuovo</th><th className="text-right">Risparmio €</th><th className="text-right">%</th><th></th></tr>
                  </thead>
                  <tbody>
                    {compareResults.results.map(r => (
                      <tr key={r.offer_id} className={r.is_best ? 'bg-green-100 font-bold' : ''} data-testid={`compare-row-${r.offer_id}`}>
                        <td className="py-2">{r.is_best && '⭐ '}{r.offer_name || r.offer_id}</td>
                        {r.error ? <td colSpan={5} className="text-red-600 text-xs">{r.error}</td> : (
                          <>
                            <td className="text-right">{r.totale_annuo_attuale?.toFixed(2)} €</td>
                            <td className="text-right">{r.totale_annuo_nuovo?.toFixed(2)} €</td>
                            <td className={`text-right ${(r.risparmio_annuo_eur || 0) > 0 ? 'text-green-700' : 'text-red-700'}`}>
                              {(r.risparmio_annuo_eur || 0) > 0 ? '−' : '+'}{Math.abs(r.risparmio_annuo_eur || 0).toFixed(2)} €
                            </td>
                            <td className="text-right">{Math.abs(r.risparmio_pct || 0).toFixed(1)}%</td>
                            <td className="text-right">
                              <Button size="sm" variant={r.is_best ? 'default' : 'outline'}
                                      onClick={() => pickBestFromCompare(r.offer_id)}
                                      className={r.is_best ? 'bg-green-600 hover:bg-green-700 text-white text-xs h-7' : 'text-xs h-7'}
                                      data-testid={`pick-cte-${r.offer_id}`}>
                                Usa questa
                              </Button>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Sprint B - Extra Sconto Agente */}
            {selectedOfferId && (
              <div className="border rounded-lg p-3 bg-amber-50 border-amber-200" data-testid="extra-sconto-block">
                <div className="flex items-center gap-2 mb-2">
                  <CurrencyEur size={16} weight="bold" className="text-amber-700" />
                  <Label className="font-semibold text-amber-900">Extra sconto agente (€/anno)</Label>
                  {isSubAgente && (
                    <Badge variant="outline" className="text-xs border-amber-400 text-amber-700">
                      richiede approvazione
                    </Badge>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    type="number" step="0.01" min="0"
                    placeholder="0,00"
                    value={extraSconto}
                    onChange={(e) => { setExtraSconto(e.target.value); if (!extraScontoRequestId) {/* nothing */} }}
                    disabled={!!extraScontoRequestId}
                    className="max-w-[160px]"
                    data-testid="extra-sconto-input"
                  />
                  <span className="text-xs text-slate-500">applicato in detrazione sulla materia annua</span>
                  {extraScontoRequestId && (
                    <Badge className="bg-green-100 text-green-800 border-green-300" data-testid="extra-sconto-approved-badge">
                      ✓ Approvato — applicato
                    </Badge>
                  )}
                </div>

                {isSubAgente && !extraScontoRequestId && (
                  <div className="mt-3 flex flex-wrap gap-2 items-center">
                    <Button
                      size="sm"
                      onClick={() => setReqDialogOpen(true)}
                      disabled={!extraSconto || parseFloat(extraSconto) <= 0}
                      className="bg-amber-600 hover:bg-amber-700"
                      data-testid="request-extra-sconto-btn"
                    >
                      <ChatCenteredText size={14} className="mr-1" /> Richiedi approvazione al mio agente
                    </Button>
                    {extraScontoRequests.filter(r => r.status === 'pending').map(r => (
                      <Badge key={r.id} variant="outline" className="border-amber-400 text-amber-700">
                        <Clock size={12} className="mr-1" /> In attesa: €{Number(r.importo_richiesto_eur_anno).toFixed(2)}
                      </Badge>
                    ))}
                    {extraScontoRequests.filter(r => r.status === 'rejected').slice(0, 1).map(r => (
                      <Badge key={r.id} variant="outline" className="border-red-300 text-red-700">
                        ✗ Rifiutata · {r.review_note}
                      </Badge>
                    ))}
                  </div>
                )}
                {!isSubAgente && !canApplyExtraScontoDirectly && (
                  <div className="text-xs text-slate-500 mt-1">Solo gli agenti possono applicare un extra sconto.</div>
                )}
              </div>
            )}

            {simulation && (
              <div className="border rounded-lg p-4">
                {simulation.warnings && simulation.warnings.length > 0 && (
                  <div className="mb-3 border-l-4 border-amber-500 bg-amber-50 p-3 rounded-r"
                       data-testid="sim-warnings-banner">
                    <div className="text-xs font-bold text-amber-800 mb-1">
                      ⚠️ {simulation.warnings.length} anomalia/e rilevata/e nel calcolo automatico
                    </div>
                    <ul className="text-xs text-amber-900 space-y-1 list-disc list-inside">
                      {simulation.warnings.map((w, i) => (
                        <li key={i}>
                          <strong className="capitalize">{w.field?.replace(/_/g, ' ')}:</strong> {w.msg}
                        </li>
                      ))}
                    </ul>
                    <div className="text-[11px] text-amber-700 mt-2">
                      I valori restano editabili manualmente sotto. Per un fix permanente, controlla la
                      configurazione dell&apos;offerta CTE oppure i valori OCR estratti dalla fattura.
                    </div>
                  </div>
                )}
                <div className="text-[10px] text-slate-500 mb-2">
                  ✏️ Tutti i campi sono editabili — clicca sul valore per modificarlo a mano (bypass calcolo automatico)
                  {simulation.manual_overrides_at && (
                    <span className="ml-2 text-amber-700">· Override manuale del {simulation.manual_overrides_at.slice(0, 16).replace('T', ' ')}</span>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="bg-slate-100 p-3 rounded text-center">
                    <div className="text-xs text-slate-500">Spesa attuale annua</div>
                    <input type="number" step="0.01"
                           key={`tot-att-${simulation.manual_overrides_at || ''}-${simulation.totale_annuo_attuale}`}
                           defaultValue={(simulation.totale_annuo_attuale || 0).toFixed(2)}
                           onBlur={e => e.target.value !== (simulation.totale_annuo_attuale || 0).toFixed(2) && overrideSimField('totale_annuo_attuale', e.target.value)}
                           className="text-xl font-bold bg-transparent w-full text-center border-b-2 border-dashed border-slate-300 hover:border-slate-500 focus:outline-none focus:border-slate-700"
                           data-testid="edit-totale-attuale" />
                    <div className="text-[10px] text-slate-400 mt-1">€/anno</div>
                  </div>
                  <div className="bg-orange-100 p-3 rounded text-center">
                    <div className="text-xs text-slate-500">Con la nostra offerta</div>
                    <input type="number" step="0.01"
                           key={`tot-nuo-${simulation.manual_overrides_at || ''}-${simulation.totale_annuo_nuovo}`}
                           defaultValue={(simulation.totale_annuo_nuovo || 0).toFixed(2)}
                           onBlur={e => e.target.value !== (simulation.totale_annuo_nuovo || 0).toFixed(2) && overrideSimField('totale_annuo_nuovo', e.target.value)}
                           className="text-xl font-bold bg-transparent w-full text-center border-b-2 border-dashed border-orange-300 hover:border-orange-500 focus:outline-none focus:border-orange-700"
                           data-testid="edit-totale-nuovo" />
                    <div className="text-[10px] text-slate-400 mt-1">€/anno</div>
                  </div>
                  <div className={`${simulation.risparmio_annuo_eur > 0 ? 'bg-green-100' : 'bg-red-100'} p-3 rounded text-center`}>
                    <div className="text-xs text-slate-500">{simulation.risparmio_annuo_eur > 0 ? 'Risparmio annuo' : 'Maggior costo'}</div>
                    <input type="number" step="0.01"
                           key={`risp-${simulation.manual_overrides_at || ''}-${simulation.risparmio_annuo_eur}`}
                           defaultValue={(simulation.risparmio_annuo_eur || 0).toFixed(2)}
                           onBlur={e => e.target.value !== (simulation.risparmio_annuo_eur || 0).toFixed(2) && overrideSimField('risparmio_annuo_eur', e.target.value)}
                           className={`text-xl font-bold bg-transparent w-full text-center border-b-2 border-dashed ${simulation.risparmio_annuo_eur > 0 ? 'border-green-300 hover:border-green-500 text-green-700' : 'border-red-300 hover:border-red-500 text-red-700'} focus:outline-none`}
                           data-testid="edit-risparmio" />
                    <div className="text-[10px] text-slate-400 mt-1">
                      <input type="number" step="0.1"
                             key={`risp-pct-${simulation.manual_overrides_at || ''}-${simulation.risparmio_pct}`}
                             defaultValue={(simulation.risparmio_pct || 0).toFixed(1)}
                             onBlur={e => e.target.value !== (simulation.risparmio_pct || 0).toFixed(1) && overrideSimField('risparmio_pct', e.target.value)}
                             className="bg-transparent w-12 text-center border-b border-dashed inline focus:outline-none"
                             data-testid="edit-risparmio-pct" />%
                    </div>
                  </div>
                </div>
                <div className="text-xs text-slate-600 grid grid-cols-2 gap-2">
                  <div>Consumo anno (kWh):
                    <input type="number" step="1"
                           key={`cons-${simulation.manual_overrides_at || ''}-${simulation.consumo_anno_kwh}`}
                           defaultValue={(simulation.consumo_anno_kwh || 0).toFixed(0)}
                           onBlur={e => e.target.value !== (simulation.consumo_anno_kwh || 0).toFixed(0) && overrideSimField('consumo_anno_kwh', e.target.value)}
                           className="font-bold bg-transparent w-24 text-right border-b border-dashed ml-1 focus:outline-none"
                           data-testid="edit-consumo-anno" />
                  </div>
                  <div>CTE: <strong>{simulation.offer_name}</strong></div>
                  <div>Prezzo medio attuale (€/kWh):
                    <input type="number" step="0.0001"
                           key={`pma-${simulation.manual_overrides_at || ''}-${simulation.prezzo_medio_attuale}`}
                           defaultValue={(simulation.prezzo_medio_attuale || 0).toFixed(4)}
                           onBlur={e => e.target.value !== (simulation.prezzo_medio_attuale || 0).toFixed(4) && overrideSimField('prezzo_medio_attuale', e.target.value)}
                           className="font-bold bg-transparent w-24 text-right border-b border-dashed ml-1 focus:outline-none"
                           data-testid="edit-prezzo-attuale" />
                    {simulation.prezzo_medio_attuale_fonte === 'letto_diretto' ? (
                      <span className="ml-2 inline-block text-[9px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-full font-medium"
                            title={simulation.prezzo_medio_attuale_origine_label}
                            data-testid="prezzo-fonte-letto">✓ Letto da bolletta</span>
                    ) : (
                      <span className="ml-2 inline-block text-[9px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full font-medium"
                            title={simulation.prezzo_medio_attuale_origine_label}
                            data-testid="prezzo-fonte-calcolato">⚠ Calcolato</span>
                    )}
                    {(simulation.prezzo_medio_vendita_kwh_attuale || simulation.prezzo_medio_rete_oneri_kwh_attuale) && (
                      <div className="text-[10px] text-slate-500 mt-1 ml-1">
                        {simulation.prezzo_medio_vendita_kwh_attuale && (
                          <span>vendita energia: <strong>{Number(simulation.prezzo_medio_vendita_kwh_attuale).toFixed(4)}</strong> €/kWh</span>
                        )}
                        {simulation.prezzo_medio_rete_oneri_kwh_attuale && (
                          <span className="ml-3">rete+oneri: <strong>{Number(simulation.prezzo_medio_rete_oneri_kwh_attuale).toFixed(4)}</strong> €/kWh</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div>Prezzo medio nuovo (€/kWh):
                    <input type="number" step="0.0001"
                           key={`pmn-${simulation.manual_overrides_at || ''}-${simulation.prezzo_medio_nuovo}`}
                           defaultValue={(simulation.prezzo_medio_nuovo || 0).toFixed(4)}
                           onBlur={e => e.target.value !== (simulation.prezzo_medio_nuovo || 0).toFixed(4) && overrideSimField('prezzo_medio_nuovo', e.target.value)}
                           className="font-bold bg-transparent w-24 text-right border-b border-dashed ml-1 focus:outline-none"
                           data-testid="edit-prezzo-nuovo" />
                  </div>
                  {simulation.extra_sconto_eur_anno > 0 && (
                    <div className="col-span-2 text-amber-700">
                      <CurrencyEur size={12} className="inline" /> Extra sconto applicato:{' '}
                      <strong>{simulation.extra_sconto_eur_anno.toFixed(2)} €/anno</strong>
                      {simulation.extra_sconto_source === 'approved_request' && ' (da richiesta approvata)'}
                    </div>
                  )}
                </div>

                {/* Sprint 47 quater — Confronto dettagliato per fascia (apple-to-apple) */}
                <div className="mt-4 border rounded-lg overflow-hidden bg-white" data-testid="apple-to-apple-table">
                  <div className="bg-slate-50 px-3 py-2 border-b flex items-center justify-between">
                    <div className="text-sm font-semibold text-slate-800">
                      🔍 Confronto dettagliato (sola vendita energia — apple-to-apple)
                    </div>
                    <span className="text-[10px] text-slate-500">
                      Modifica i valori e clicca <strong>Applica modifiche</strong>.
                    </span>
                  </div>
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100 text-slate-600">
                      <tr>
                        <th className="text-left px-3 py-1.5">Voce</th>
                        <th className="text-right px-3 py-1.5">Attuale (ENEL/competitor)</th>
                        <th className="text-right px-3 py-1.5">Nuova (nostra CTE)</th>
                        <th className="text-right px-3 py-1.5">Δ</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {/* F1 */}
                      <tr>
                        <td className="px-3 py-1.5 text-slate-700">F1 — ore picco</td>
                        <td className="px-3 py-1.5 text-right">
                          <input type="number" step="1" key={`cF1-${simulation.manual_overrides_at || ''}-${simulation.consumo_F1_kwh}`} defaultValue={(simulation.consumo_F1_kwh || 0).toFixed(0)}
                                 onChange={e => setPending('consumo_F1_kwh', e.target.value)}
                                 className="w-16 text-right bg-transparent border-b border-dashed"
                                 data-testid="edit-consumo-F1" /> kWh ×
                          <input type="number" step="0.0001" key={`pF1-${simulation.manual_overrides_at || ''}-${simulation.prezzo_attuale_F1}`} defaultValue={(simulation.prezzo_attuale_F1 || 0).toFixed(4)}
                                 onChange={e => setPending('prezzo_attuale_F1', e.target.value)}
                                 className="w-20 text-right bg-transparent border-b border-dashed ml-1"
                                 data-testid="edit-prezzo-attuale-F1" /> €
                        </td>
                        <td className="px-3 py-1.5 text-right text-slate-500">calcolato da CTE</td>
                        <td className="px-3 py-1.5 text-right font-medium"></td>
                      </tr>
                      {/* F2 */}
                      <tr>
                        <td className="px-3 py-1.5 text-slate-700">F2 — ore intermedie</td>
                        <td className="px-3 py-1.5 text-right">
                          <input type="number" step="1" key={`cF2-${simulation.manual_overrides_at || ''}-${simulation.consumo_F2_kwh}`} defaultValue={(simulation.consumo_F2_kwh || 0).toFixed(0)}
                                 onChange={e => setPending('consumo_F2_kwh', e.target.value)}
                                 className="w-16 text-right bg-transparent border-b border-dashed"
                                 data-testid="edit-consumo-F2" /> kWh ×
                          <input type="number" step="0.0001" key={`pF2-${simulation.manual_overrides_at || ''}-${simulation.prezzo_attuale_F2}`} defaultValue={(simulation.prezzo_attuale_F2 || 0).toFixed(4)}
                                 onChange={e => setPending('prezzo_attuale_F2', e.target.value)}
                                 className="w-20 text-right bg-transparent border-b border-dashed ml-1"
                                 data-testid="edit-prezzo-attuale-F2" /> €
                        </td>
                        <td className="px-3 py-1.5 text-right text-slate-500">calcolato da CTE</td>
                        <td className="px-3 py-1.5 text-right"></td>
                      </tr>
                      {/* F3 */}
                      <tr>
                        <td className="px-3 py-1.5 text-slate-700">F3 — ore fuori picco</td>
                        <td className="px-3 py-1.5 text-right">
                          <input type="number" step="1" key={`cF3-${simulation.manual_overrides_at || ''}-${simulation.consumo_F3_kwh}`} defaultValue={(simulation.consumo_F3_kwh || 0).toFixed(0)}
                                 onChange={e => setPending('consumo_F3_kwh', e.target.value)}
                                 className="w-16 text-right bg-transparent border-b border-dashed"
                                 data-testid="edit-consumo-F3" /> kWh ×
                          <input type="number" step="0.0001" key={`pF3-${simulation.manual_overrides_at || ''}-${simulation.prezzo_attuale_F3}`} defaultValue={(simulation.prezzo_attuale_F3 || 0).toFixed(4)}
                                 onChange={e => setPending('prezzo_attuale_F3', e.target.value)}
                                 className="w-20 text-right bg-transparent border-b border-dashed ml-1"
                                 data-testid="edit-prezzo-attuale-F3" /> €
                        </td>
                        <td className="px-3 py-1.5 text-right text-slate-500">calcolato da CTE</td>
                        <td className="px-3 py-1.5 text-right"></td>
                      </tr>
                      {/* Dispacciamento */}
                      <tr className="bg-amber-50/40">
                        <td className="px-3 py-1.5 text-slate-700">+ Dispacciamento (€/kWh)</td>
                        <td className="px-3 py-1.5 text-right text-slate-500">incluso in prezzo</td>
                        <td className="px-3 py-1.5 text-right">
                          <input type="number" step="0.0001"
                                 key={`disp-${simulation.manual_overrides_at || ''}-${simulation.dispacciamento_eur_kwh}`}
                                 defaultValue={(simulation.dispacciamento_eur_kwh || 0).toFixed(4)}
                                 onChange={e => setPending('dispacciamento_eur_kwh', e.target.value)}
                                 className="w-20 text-right bg-transparent border-b border-dashed border-amber-500 font-medium"
                                 data-testid="edit-dispacciamento" /> €/kWh
                        </td>
                        <td className="px-3 py-1.5 text-right"></td>
                      </tr>
                      {/* Capacity market */}
                      <tr className="bg-amber-50/40">
                        <td className="px-3 py-1.5 text-slate-700">+ Capacity market (€/kWh)</td>
                        <td className="px-3 py-1.5 text-right text-slate-500">incluso in prezzo</td>
                        <td className="px-3 py-1.5 text-right">
                          <input type="number" step="0.0001"
                                 key={`cap-${simulation.manual_overrides_at || ''}-${simulation.capacity_market_eur_kwh}`}
                                 defaultValue={(simulation.capacity_market_eur_kwh || 0).toFixed(4)}
                                 onChange={e => setPending('capacity_market_eur_kwh', e.target.value)}
                                 className="w-20 text-right bg-transparent border-b border-dashed border-amber-500 font-medium"
                                 data-testid="edit-capacity-market" /> €/kWh
                        </td>
                        <td className="px-3 py-1.5 text-right text-slate-500">
                          +{(simulation.extra_unitario_anno_eur || 0).toFixed(2)} €/anno
                        </td>
                      </tr>
                      {/* Quota fissa vendita */}
                      <tr className="bg-blue-50/40">
                        <td className="px-3 py-1.5 text-slate-700">Quota fissa vendita (€/mese)</td>
                        <td className="px-3 py-1.5 text-right">
                          <input type="number" step="0.01"
                                 key={`qfv-${simulation.manual_overrides_at || ''}-${simulation.quota_fissa_vendita_mese_attuale}`}
                                 defaultValue={(simulation.quota_fissa_vendita_mese_attuale || 0).toFixed(2)}
                                 onChange={e => setPending('quota_fissa_vendita_mese_attuale', e.target.value)}
                                 className="w-20 text-right bg-transparent border-b border-dashed"
                                 data-testid="edit-quota-fissa-vendita" /> €/mese
                        </td>
                        <td className="px-3 py-1.5 text-right text-slate-500">
                          {((simulation.pcv_qvd_anno_nuovo || 0) / 12).toFixed(2)} €/mese (PCV/QVD)
                        </td>
                        <td className="px-3 py-1.5 text-right"></td>
                      </tr>
                      {/* Quota fissa rete+oneri (info) */}
                      <tr className="bg-slate-50 text-slate-500 text-[11px]">
                        <td className="px-3 py-1.5">Quota fissa rete+oneri (regolato ARERA)</td>
                        <td className="px-3 py-1.5 text-right">
                          {(simulation.quota_fissa_rete_oneri_mese_attuale || 0).toFixed(2)} €/mese
                        </td>
                        <td className="px-3 py-1.5 text-right">uguale (regolato)</td>
                        <td className="px-3 py-1.5 text-right text-slate-400">—</td>
                      </tr>
                      {/* TOTALI */}
                      <tr className="bg-slate-100 font-bold text-slate-800">
                        <td className="px-3 py-2">TOTALE materia annua (sola vendita)</td>
                        <td className="px-3 py-2 text-right">
                          {(simulation.materia_attuale_anno || 0).toFixed(2)} €
                        </td>
                        <td className="px-3 py-2 text-right">
                          {(simulation.materia_nuova_anno || 0).toFixed(2)} €
                        </td>
                        <td className={`px-3 py-2 text-right ${(simulation.risparmio_annuo_eur || 0) > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {(simulation.risparmio_annuo_eur || 0) > 0 ? '−' : '+'}
                          {Math.abs(simulation.risparmio_annuo_eur || 0).toFixed(2)} €
                          {' '}({Math.abs(simulation.risparmio_pct || 0).toFixed(1)}%)
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  {/* Barra "Applica modifiche" */}
                  <div className={`px-3 py-2 border-t flex items-center justify-between ${hasPending ? 'bg-amber-50' : 'bg-slate-50'}`}>
                    <div className="text-xs">
                      {hasPending ? (
                        <span className="text-amber-700 font-medium">
                          ⚠️ {Object.keys(pendingOverrides).length} modifiche non applicate
                          <span className="text-slate-500 ml-2">
                            ({Object.keys(pendingOverrides).join(', ')})
                          </span>
                        </span>
                      ) : (
                        <span className="text-slate-500">Nessuna modifica in sospeso</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {hasPending && (
                        <button onClick={cancelPendingOverrides} disabled={applying}
                                className="px-3 py-1 text-xs text-slate-600 hover:bg-white rounded"
                                data-testid="cancel-pending-overrides">
                          Annulla
                        </button>
                      )}
                      <button onClick={applyPendingOverrides}
                              disabled={!hasPending || applying}
                              className={`px-4 py-1.5 text-xs font-medium rounded transition-colors ${
                                hasPending && !applying
                                  ? 'bg-orange-500 hover:bg-orange-600 text-white'
                                  : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                              }`}
                              data-testid="apply-pending-overrides">
                        {applying ? 'Applicazione…' : '✓ Applica modifiche'}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <Button onClick={openProposalDialog} className="bg-orange-500 hover:bg-orange-600"
                          data-testid="generate-proposal-btn">
                    <FilePdf size={16} className="mr-2" /> Genera proposta cliente
                  </Button>
                  <span className="text-xs text-slate-500 self-center">
                    (genera la Lettera PDF + crea/aggiorna il Lead in un click)
                  </span>
                  <Button onClick={openLetterOnlyDialog} variant="outline"
                          className="border-orange-300 text-orange-700 hover:bg-orange-50"
                          data-testid="generate-letter-only-btn">
                    <FilePdf size={16} className="mr-2" /> Solo Lettera di accompagno (PDF)
                  </Button>
                  <Button onClick={() => setShowFormula(v => !v)} variant="outline"
                          className="border-purple-300 text-purple-700 hover:bg-purple-50 ml-auto"
                          data-testid="toggle-formula-btn">
                    <Calculator size={16} className="mr-2" /> {showFormula ? 'Nascondi formula' : 'Mostra formula'}
                  </Button>
                </div>

                {showFormula && (
                  <div className="mt-4 bg-purple-50 border border-purple-200 rounded-lg p-4 text-xs font-mono"
                       data-testid="formula-panel">
                    <div className="text-purple-700 font-semibold mb-2">🧮 Trasparenza calcolo</div>
                    <div className="grid grid-cols-2 gap-2 text-slate-700">
                      <div>Consumo annuo (kWh):</div><div className="text-right">{simulation.consumo_anno_kwh?.toFixed(0)}</div>
                      <div>Materia prezzo unitario attuale (€/kWh):</div><div className="text-right">{simulation.prezzo_medio_attuale?.toFixed(4)}</div>
                      <div>Materia prezzo unitario nuovo (€/kWh):</div><div className="text-right">{simulation.prezzo_medio_nuovo?.toFixed(4)}</div>
                      <div className="border-t pt-1">Materia attuale × consumo =</div>
                      <div className="text-right border-t pt-1">{((simulation.prezzo_medio_attuale || 0) * (simulation.consumo_anno_kwh || 0)).toFixed(2)} €</div>
                      <div>Materia nuova × consumo =</div>
                      <div className="text-right">{((simulation.prezzo_medio_nuovo || 0) * (simulation.consumo_anno_kwh || 0)).toFixed(2)} €</div>
                      <div className="border-t pt-1 font-semibold text-purple-800">Risparmio materia (sola voce vendita):</div>
                      <div className="text-right border-t pt-1 font-semibold text-purple-800">{simulation.risparmio_annuo_eur?.toFixed(2)} € ({simulation.risparmio_pct?.toFixed(2)}%)</div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-purple-200 text-purple-700">
                      <strong>Logica:</strong> sostituiamo solo la voce &quot;Servizi di vendita&quot; (PCV/QVD) dalla fattura
                      con il nostro valore. Trasporto e Oneri di sistema sono regolati ARERA e identici tra operatori →
                      restano invariati nei due totali. CTE applicata: <strong>{simulation.offer_name}</strong>.
                    </div>
                  </div>
                )}

                <SpiegaVoceBar billContext={{
                  fornitore_attuale: review?.fornitore_attuale,
                  consumo_anno_kwh: simulation.consumo_anno_kwh,
                  totale_attuale: simulation.totale_annuo_attuale,
                }} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStep('review')}>← Indietro</Button>
            <Button variant="ghost" onClick={() => { setStep('list'); setActive(null); }}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sprint 40 - Workflow unificato: Genera proposta + Crea lead in un solo passo */}
      <Dialog open={proposalDialogOpen} onOpenChange={(o) => !proposalSubmitting && setProposalDialogOpen(o)}>
        <DialogContent className="max-w-lg" data-testid="proposal-dialog">
          <DialogHeader>
            <DialogTitle><FilePdf className="inline mr-2 text-orange-500" /> Genera proposta cliente</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-slate-600 bg-orange-50 border border-orange-200 rounded p-2">
              In un solo passo: <strong>1)</strong> genero la Lettera Comparativa PDF, <strong>2)</strong> creo o aggiorno il Lead,
              <strong> 3)</strong> alleggo il PDF al Lead (visibile nel suo tab &quot;Fatture&quot;).
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Email cliente</Label>
                <Input type="email" value={proposalForm.email}
                       onChange={(e) => setProposalForm(p => ({ ...p, email: e.target.value }))}
                       data-testid="proposal-email" />
              </div>
              <div>
                <Label>Telefono</Label>
                <Input type="tel" value={proposalForm.telefono}
                       onChange={(e) => setProposalForm(p => ({ ...p, telefono: e.target.value }))}
                       data-testid="proposal-telefono" />
              </div>
            </div>
            <p className="text-xs text-slate-500">Almeno uno tra email e telefono è obbligatorio per creare il lead.</p>

            <div className="border-t pt-3">
              <div className="text-sm font-semibold mb-2">🔒 Consensi GDPR</div>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <Checkbox checked={proposalForm.trattamento_dati}
                          onCheckedChange={(v) => setProposalForm(p => ({ ...p, trattamento_dati: !!v }))}
                          data-testid="proposal-trattamento" className="mt-1" />
                <span><strong>Trattamento dei dati personali</strong> (GDPR art.6) — obbligatorio per generare la lettera</span>
              </label>
              <label className="flex items-start gap-2 text-sm cursor-pointer mt-2">
                <Checkbox checked={proposalForm.rivendibilita_dati}
                          onCheckedChange={(v) => setProposalForm(p => ({ ...p, rivendibilita_dati: !!v }))}
                          data-testid="proposal-rivendibilita" className="mt-1" />
                <span>Comunicazione dati a terzi e rivendibilità</span>
              </label>
              <label className="flex items-start gap-2 text-sm cursor-pointer mt-2">
                <Checkbox checked={proposalForm.marketing}
                          onCheckedChange={(v) => setProposalForm(p => ({ ...p, marketing: !!v }))}
                          data-testid="proposal-marketing" className="mt-1" />
                <span>Comunicazioni commerciali e marketing</span>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setProposalDialogOpen(false)} disabled={proposalSubmitting}>
              Annulla
            </Button>
            <Button onClick={submitProposalAndLead} disabled={proposalSubmitting}
                    className="bg-orange-500 hover:bg-orange-600"
                    data-testid="submit-proposal-btn">
              {proposalSubmitting ? 'Elaboro…' : 'Genera PDF + Crea Lead'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sprint 47 quinquies — Dialog "Solo Lettera di accompagno" */}
      <Dialog open={letterDialogOpen} onOpenChange={(o) => !letterSubmitting && setLetterDialogOpen(o)}>
        <DialogContent className="max-w-lg" data-testid="letter-only-dialog">
          <DialogHeader>
            <DialogTitle><FilePdf className="inline mr-2 text-orange-500" /> Genera lettera di accompagno</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-slate-600 bg-orange-50 border border-orange-200 rounded p-2">
              Genera <strong>solo</strong> la Lettera Comparativa PDF (senza creare/aggiornare il Lead).
              Se selezioni gli allegati, verranno aperti in tab separati pronti per il download.
            </div>
            <div className="border-t pt-3">
              <div className="text-sm font-semibold mb-2">📎 Allegati da includere</div>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <Checkbox checked={letterForm.include_bolletta}
                          onCheckedChange={(v) => setLetterForm(p => ({ ...p, include_bolletta: !!v }))}
                          data-testid="letter-include-bolletta" className="mt-1" />
                <span><strong>Bolletta originale</strong> (PDF caricato in OCR)</span>
              </label>
              <label className="flex items-start gap-2 text-sm cursor-pointer mt-2">
                <Checkbox checked={letterForm.include_doc_identita}
                          onCheckedChange={(v) => setLetterForm(p => ({ ...p, include_doc_identita: !!v }))}
                          data-testid="letter-include-doc-identita" className="mt-1" />
                <span><strong>Documento d&apos;identità</strong> (se caricato sul lead collegato)</span>
              </label>
            </div>
            <div className="border-t pt-3">
              <div className="text-sm font-semibold mb-2">🔒 Consensi GDPR</div>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <Checkbox checked={letterForm.trattamento_dati}
                          onCheckedChange={(v) => setLetterForm(p => ({ ...p, trattamento_dati: !!v }))}
                          data-testid="letter-trattamento" className="mt-1" />
                <span><strong>Trattamento dei dati personali</strong> (GDPR art.6) — obbligatorio per generare la lettera</span>
              </label>
              <label className="flex items-start gap-2 text-sm cursor-pointer mt-2">
                <Checkbox checked={letterForm.rivendibilita_dati}
                          onCheckedChange={(v) => setLetterForm(p => ({ ...p, rivendibilita_dati: !!v }))}
                          data-testid="letter-rivendibilita" className="mt-1" />
                <span>Comunicazione dati a terzi e rivendibilità</span>
              </label>
              <label className="flex items-start gap-2 text-sm cursor-pointer mt-2">
                <Checkbox checked={letterForm.marketing}
                          onCheckedChange={(v) => setLetterForm(p => ({ ...p, marketing: !!v }))}
                          data-testid="letter-marketing" className="mt-1" />
                <span>Comunicazioni commerciali e marketing</span>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setLetterDialogOpen(false)} disabled={letterSubmitting}>
              Annulla
            </Button>
            <Button onClick={submitLetterOnly} disabled={letterSubmitting}
                    className="bg-orange-500 hover:bg-orange-600"
                    data-testid="submit-letter-only-btn">
              {letterSubmitting ? 'Elaboro…' : 'Genera Lettera PDF'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sprint B - Dialog richiesta extra sconto (sub-agente) */}
      <Dialog open={reqDialogOpen} onOpenChange={setReqDialogOpen}>        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Richiedi extra sconto</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-slate-600 bg-amber-50 border border-amber-200 rounded p-3">
              La richiesta verrà inviata al tuo agente di riferimento e agli amministratori.
              Riceverai notifica nella sezione <strong>&quot;Approvazioni&quot;</strong> non appena verrà gestita.
            </div>
            <div>
              <Label>Importo richiesto (€/anno)</Label>
              <Input type="number" step="0.01" min="0" value={extraSconto}
                     onChange={(e) => setExtraSconto(e.target.value)}
                     data-testid="dialog-extra-sconto-amount" />
            </div>
            <div>
              <Label>Motivazione (min 5 caratteri)</Label>
              <Textarea rows={3} value={extraScontoMotivo}
                        onChange={(e) => setExtraScontoMotivo(e.target.value)}
                        placeholder="Es. cliente strategico, volume elevato, fidelizzazione…"
                        data-testid="dialog-extra-sconto-motivo" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReqDialogOpen(false)}>Annulla</Button>
            <Button onClick={submitExtraScontoRequest} className="bg-amber-600 hover:bg-amber-700"
                    data-testid="submit-extra-sconto-request-btn">
              Invia richiesta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
